import {
  Card,
  IconCheckmark,
  IconX,
  OptionInterface,
  Table,
  TableColumnWidth,
  TableColumns,
  TableData,
  TablePagination,
  TablePreferences,
  Tabs,
} from "@veneer/core";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/views/sellOut/sellOut.css";
import { IScoreCard } from "../../models/input/sellOutApi/IScoreCard";
import SellOutService from "../../services/sellOutApi/sellOut.service";
import { IScoreCardDto } from "../../models/dto/sellOutApi/IScoreCardDto";

export default function Scorecard() {
  const [t] = useTranslation("global");
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedTabIdDefault, setSelectedTabIdDefault] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectDate, setSelectDate] = useState<Date | null>(new Date());

  const [formScoreCard, setFormScoreCard] = useState<IScoreCard>({
    StartDate: "",
    FirstSunday: "",
    SecondSunday: "",
    ThirdSunday: "",
    FourthSunday: "",
    FifthSunday: "",
    UserID: 0,
  });

  useEffect(() => {
    setSelectedTabIdDefault(1);

    if (selectDate) {
      const firstDayOfMonth = new Date(
        selectDate.getFullYear(),
        selectDate.getMonth(),
        1
      );
      let firstSunday = new Date(firstDayOfMonth);

      while (firstSunday.getDay() !== 0) {
        firstSunday.setDate(firstSunday.getDate() + 1);
      }

      const sundaysInMonth: any = [];
      let currentSunday = new Date(firstSunday);

      while (currentSunday.getMonth() === selectDate.getMonth()) {
        sundaysInMonth.push(new Date(currentSunday));
        currentSunday.setDate(currentSunday.getDate() + 7);
      }

      if (sundaysInMonth.length >= 4) {
        const nextMonth = (selectDate.getMonth() + 1) % 12;
        const nextMonthYear =
          nextMonth === 0
            ? selectDate.getFullYear() + 1
            : selectDate.getFullYear();
        const nextMonthFirstDay = new Date(nextMonthYear, nextMonth, 1);
        let nextMonthFirstSunday = new Date(nextMonthFirstDay);

        while (nextMonthFirstSunday.getDay() !== 0) {
          nextMonthFirstSunday.setDate(nextMonthFirstSunday.getDate() + 1);
        }
        sundaysInMonth.push(nextMonthFirstSunday);
      }

      setFormScoreCard((prevState) => ({
        ...prevState,
        StartDate: formattedDate(firstDayOfMonth),
        FirstSunday: formattedDate(sundaysInMonth[0] || new Date()),
        SecondSunday: formattedDate(sundaysInMonth[1] || new Date()),
        ThirdSunday: formattedDate(sundaysInMonth[2] || new Date()),
        FourthSunday: formattedDate(sundaysInMonth[3] || new Date()),
        FifthSunday: formattedDate(sundaysInMonth[4] || new Date()),
      }));
    }
  }, [selectDate]);

  useEffect(() => {
    getScoreCard(formScoreCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formScoreCard]);

  let formattedDate = (date: Date) => {
    let year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();

    let monthStr = month < 10 ? `0${month}` : `${month}`;
    let dayStr = day < 10 ? `0${day}` : `${day}`;

    return `${dayStr}/${monthStr}/${year}`;
  };

  //Configuracion de la tabla
  const columnsWidth: TableColumnWidth[] = [
    { columnId: "country" },
    { columnId: "subRetail" },
    { columnId: "firstSunday" },
    { columnId: "secondSunday" },
    { columnId: "thirdSunday" },
    { columnId: "fourthSunday" },
    { columnId: "fifthSunday" },
  ];

  const tablePreferences: TablePreferences = {
    width: columnsWidth,
  };

  const columns1: TableColumns[] = [
    { id: "country", label: t("header.country") },
    { id: "subRetail", label: t("header.subRetail") },
    { id: "firstSunday", label: formScoreCard.FirstSunday },
    { id: "secondSunday", label: formScoreCard.SecondSunday },
    { id: "thirdSunday", label: formScoreCard.ThirdSunday },
    { id: "fourthSunday", label: formScoreCard.FourthSunday },
    { id: "fifthSunday", label: formScoreCard.FifthSunday },
  ];

  const pagedData = tableData.slice(
    (currentPage - 1) * pageSize,
    (currentPage - 1) * pageSize + pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => {
    setPageSize(option.value);
  };

  const paginationProps: TablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: tableData.length,
  };

  //Tabs
  const onChangeTab = (value: number) => {
    if (value === 1) {
      cleanAll();
      setSelectedTabIdDefault(5);
    } else {
      cleanAll();
      setSelectedTabIdDefault(1);
    }
  };

  //Request
  const getScoreCard = (form: IScoreCard) => {
    setLoading(true);
    SellOutService.getScoreCard(form).then((result: IScoreCardDto[]) => {
      fillData(result);
    });
  };

  const fillData = (data: IScoreCardDto[]) => {
    const pagedData: TableData[] = [];

    for (let i = 0; i < data.length; i++) {
      const scoreCard: IScoreCardDto = data[i];
      let tableData: TableData = {};

      tableData = {
        country: scoreCard.country,
        subRetail: scoreCard.subretail_Name,
        firstSunday:
          scoreCard.firstWeek === 1 ? (
            <IconCheckmark filled color="colorGreen4" size={30} />
          ) : (
            <IconX filled color="colorRed4" size={30} />
          ),
        secondSunday:
          scoreCard.secondWeek === 1 ? (
            <IconCheckmark filled color="colorGreen4" size={30} />
          ) : (
            <IconX filled color="colorRed4" size={30} />
          ),
        thirdSunday:
          scoreCard.thirdWeek === 1 ? (
            <IconCheckmark filled color="colorGreen4" size={30} />
          ) : (
            <IconX filled color="colorRed4" size={30} />
          ),
        fourthSunday:
          scoreCard.fourthWeek === 1 ? (
            <IconCheckmark filled color="colorGreen4" size={30} />
          ) : (
            <IconX filled color="colorRed4" size={30} />
          ),
        fifthSunday:
          scoreCard.fifthWeek === 1 ? (
            <IconCheckmark filled color="colorGreen4" size={30} />
          ) : (
            <IconX filled color="colorRed4" size={30} />
          ),

        rowConfig: {
          disabled: false,
          noWrap: false,
          selected: false,
        },
      };
      pagedData.push(tableData);
    }
    setTableData(pagedData);
    setLoading(false);
  };

  const handleChange = (date: Date | null) => {
    setSelectDate(date);
  };

  const cleanAll = () => {
    setCurrentPage(1);
    setTableData([]);
  };

  const ExampleCustomInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void; className?: string }>(
    ({ value, onClick, className }, ref) => (
      <div className={`custom-datePicker ${className}`}>
        <input
          className="inputDatePicker"
          value={value}
          onClick={onClick}
          readOnly
          ref={ref}
        />
        <div className="iconDatePicker">
          <svg
            aria-label="Calendar"
            fill="currentColor"
            focusable="false"
            role="img"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19,4.5001831h-2v-1c0-0.2763672-0.2236328-0.5-0.5-0.5s-0.5,0.2236328-0.5,0.5v3
                c0,0.2763672,0.2236328,0.5,0.5,0.5s0.5-0.2236328,0.5-0.5v-1h2c0.5517578,0,1,0.4487305,1,1v2.5H4v-2.5
                c0-0.5512695,0.4487305-1,1-1h0.5c0.2763672,0,0.5-0.2236328,0.5-0.5s-0.2236328-0.5-0.5-0.5H5c-1.1030273,0-2,0.8969727-2,2v12.5
                c0,1.1025391,0.8969727,2,2,2h14c1.1025391,0,2-0.8974609,2-2v-12.5C21,5.3971558,20.1025391,4.5001831,19,4.5001831z
                M20,19.0001831c0,0.5517578-0.4482422,1-1,1H5c-0.5512695,0-1-0.4482422-1-1v-9h16V19.0001831z M7,6.5001831v-3
                c0-0.2763672,0.2236328-0.5,0.5-0.5s0.5,0.2236328,0.5,0.5v1h6.5c0.2763672,0,0.5,0.2236328,0.5,0.5s-0.2236328,0.5-0.5,0.5H8v1
                c0,0.2763672-0.2236328,0.5-0.5,0.5S7,6.7765503,7,6.5001831z"
            ></path>
          </svg>
        </div>
      </div>
    )
  );
  
  return (
    <>
      <div style={{ padding: "1%" }}>
        <h4>ScoreCard</h4>
      </div>

      <div
        style={{
          padding: "0.8%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DatePicker
          selected={selectDate}
          onChange={handleChange}
          customInput={<ExampleCustomInput />}
          dateFormat="MM/yyyy"
          showMonthYearPicker
        />
      </div>
      <div style={{ padding: "20px" }}>
        <Tabs
          controlId="default"
          onChangeTab={() => onChangeTab(selectedTabIdDefault)}
          selectedTabId={selectedTabIdDefault}
          tabs={[
            {
              id: 1,
              label: t("header.weekly"),
              content: (
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    padding: "30px",
                  }}
                >
                  <Card
                    appearance="outlined"
                    className="divTableAttendance"
                    content={
                      <Table
                        className="tableAttendance"
                        behavior={{
                          type: "singlePage",
                        }}
                        columnResize
                        loading={loading}
                        columns={columns1}
                        data={pagedData}
                        loadingDataLength={5}
                        preferences={tablePreferences}
                        pagination={paginationProps}
                      />
                    }
                  />
                </div>
              ),
            },
          ]}
          type="attached"
        />
      </div>
    </>
  );
}
